import React, { useEffect, useMemo, useState } from 'react';
import { Plus, ChevronDown, Save, X, Loader, Sparkles, Calendar } from 'lucide-react';
import AISummaryModal from '../components/AISummaryModal';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar, ReferenceLine } from 'recharts';
import DOMPurify from 'dompurify';
import { formatMonthKey, formatMonthShort } from '../utils/dateUtils';
import { formatCurrency } from '../utils/formatters';
import { getChartData, getMonthlyChange, getGrowthInfo } from '../utils/chartUtils';
import { calculateFireNumber, calculateTimeToFire } from '../utils/fireUtils';
import { trackEvent } from '../utils/analytics';

const NET_WORTH_COLOR = '#059669';
const ASSETS_COLOR = '#2563eb';
const DEBTS_COLOR = '#e11d48';

const CHART_RANGES = [
  { key: '6m', label: '6M', months: 6 },
  { key: '1y', label: '1Y', months: 12 },
  { key: 'max', label: 'Max', months: null },
];

const CHART_STYLE = {
  contentStyle: {
    backgroundColor: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: '10px',
    fontSize: '12px',
    color: '#111827',
    boxShadow: '0 4px 12px rgba(16,24,40,0.08)',
  },
  axisStyle: { fontSize: '11px' },
  gridStroke: '#e5e7eb',
  axisStroke: '#d1d5db',
  tickFill: '#9ca3af',
};

const AccountRows = ({ items, editMode, entryData, priorValues, handleValueChange }) => (
  <div className="divide-y divide-gray-100">
    {items.map(account => (
      <div key={account.id} className="flex items-center px-5 py-3 gap-3">
        <span className="text-sm text-gray-700 flex-1 truncate min-w-0">
          {DOMPurify.sanitize(account.name)}
        </span>
        <div className="relative flex-shrink-0 w-32">
          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none">$</span>
          <input
            type="number"
            step="0.01"
            min="0"
            value={entryData[account.name] || ''}
            onChange={(e) => handleValueChange(account.name, e.target.value)}
            disabled={!editMode}
            className={`w-full pl-6 pr-2 py-1.5 text-sm outline-none rounded-lg placeholder-gray-400
              ${editMode
                ? 'bg-gray-50 border border-gray-200 text-gray-900 focus:border-accent focus:ring-2 focus:ring-emerald-100'
                : 'bg-transparent border-transparent text-gray-500'
              }`}
            placeholder={
              priorValues[account.name] != null && priorValues[account.name] !== ''
                ? parseFloat(priorValues[account.name]).toFixed(2)
                : '0.00'
            }
          />
        </div>
      </div>
    ))}
  </div>
);

const StatCard = ({ label, value, tone }) => (
  <div className="bg-white rounded-xl border border-gray-100 shadow-card px-5 py-4">
    <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5">{label}</p>
    <p className={`text-lg font-semibold tracking-tight ${
      tone === 'positive' ? 'text-accent' : tone === 'negative' ? 'text-negative' : 'text-gray-900'
    }`}>{value}</p>
  </div>
);

const EntryView = ({
  accounts,
  monthlyData,
  selectedMonth,
  editMode,
  entryData,
  saving,
  setShowAccountManager,
  startNewEntry,
  setShowMonthPicker,
  saveMonthData,
  cancelEdit,
  handleValueChange,
  fireSettings,
  updateFireSettings,
  toggleFireAsset
}) => {
  const priorValues = useMemo(() => {
    if (!selectedMonth) return {};
    const prior = [...monthlyData]
      .filter(m => m.month < selectedMonth)
      .sort((a, b) => b.month.localeCompare(a.month))[0];
    return prior?.values ?? {};
  }, [monthlyData, selectedMonth]);

  const [selectedAccount, setSelectedAccount] = useState('overall');
  const [chartRange, setChartRange] = useState('max');

  const sortedMonthlyData = useMemo(
    () => [...monthlyData].sort((a, b) => a.month.localeCompare(b.month)),
    [monthlyData]
  );

  // The chart-range toggle (6M/1Y/Max) windows the chart and the stats/growth
  // rate derived alongside it. It's a trailing window off the real latest
  // month, so "latest" is the same whether ranged or not — only the start
  // of the series moves. FIRE progress deliberately stays off the full
  // history below since it's a long-term goal, not a chart-view stat.
  const rangedMonthlyData = useMemo(() => {
    const range = CHART_RANGES.find(r => r.key === chartRange);
    if (!range?.months) return sortedMonthlyData;
    return sortedMonthlyData.slice(-range.months);
  }, [sortedMonthlyData, chartRange]);

  const hasHistory = sortedMonthlyData.length > 0;
  const chartData = hasHistory ? getChartData(rangedMonthlyData) : null;
  const monthlyChange = hasHistory ? getMonthlyChange(rangedMonthlyData) : null;
  const latestData = hasHistory ? sortedMonthlyData[sortedMonthlyData.length - 1] : null;
  const firstData = hasHistory ? rangedMonthlyData[0] : null;
  const totalGrowth = latestData && firstData ? latestData.netWorth - firstData.netWorth : 0;
  const growthPct = firstData && firstData.netWorth !== 0
    ? ((totalGrowth / Math.abs(firstData.netWorth)) * 100).toFixed(2)
    : null;

  const isCategorySelection = selectedAccount.startsWith('cat:');
  const selectedCategoryName = isCategorySelection ? selectedAccount.slice(4) : null;
  const selectedAccountLabel = selectedCategoryName ?? selectedAccount;

  const categories = useMemo(() => {
    const set = new Set();
    [...accounts.assets, ...accounts.debts].forEach(a => { if (a.category) set.add(a.category); });
    return [...set].sort();
  }, [accounts]);

  const categoryAccountNames = useMemo(() => {
    if (!selectedCategoryName) return [];
    return [...accounts.assets, ...accounts.debts]
      .filter(a => a.category === selectedCategoryName)
      .map(a => a.name);
  }, [selectedCategoryName, accounts]);

  const accountChartData = useMemo(() => {
    if (selectedAccount === 'overall' || !hasHistory) return null;

    if (isCategorySelection) {
      if (categoryAccountNames.length === 0) return [];
      return rangedMonthlyData
        .filter(m => categoryAccountNames.some(n => m.values[n] !== undefined && m.values[n] !== ''))
        .map(m => ({
          displayMonth: formatMonthShort(m.month),
          value: categoryAccountNames.reduce((sum, n) => sum + (parseFloat(m.values[n]) || 0), 0),
        }));
    }

    return rangedMonthlyData
      .filter(m => m.values[selectedAccount] !== undefined && m.values[selectedAccount] !== '')
      .map(m => ({
        displayMonth: formatMonthShort(m.month),
        value: parseFloat(m.values[selectedAccount]) || 0,
      }));
  }, [selectedAccount, rangedMonthlyData, hasHistory, isCategorySelection, categoryAccountNames]);

  const accountMonthlyChange = useMemo(() => {
    if (!accountChartData || accountChartData.length < 2) return null;
    return accountChartData.slice(1).map((d, i) => ({
      displayMonth: d.displayMonth,
      change: d.value - accountChartData[i].value,
    }));
  }, [accountChartData]);

  const accountStats = useMemo(() => {
    if (!accountChartData || accountChartData.length === 0) return null;
    const first = accountChartData[0].value;
    const last = accountChartData[accountChartData.length - 1].value;
    const change = last - first;
    return {
      current: last,
      change,
      pct: first !== 0 ? ((change / Math.abs(first)) * 100).toFixed(2) : null,
      months: accountChartData.length,
    };
  }, [accountChartData]);

  const [projectionYears, setProjectionYears] = useState(0);
  const [showAISummary, setShowAISummary] = useState(false);

  // Derive growth rate from historical data for the active series
  const growthInfo = useMemo(() => {
    if (!hasHistory) return null;
    const series = selectedAccount === 'overall'
      ? rangedMonthlyData.map(m => m.netWorth)
      : (accountChartData?.map(d => d.value) ?? []);
    return getGrowthInfo(series, { annualize: true });
  }, [hasHistory, rangedMonthlyData, selectedAccount, accountChartData]);

  // FIRE (Financial Independence, Retire Early) tracking. Not every asset
  // should count toward it (e.g. a kid's 529 plan isn't the user's own
  // retirement money), so this is computed from only the assets marked
  // includeInFire, minus all debts — not the overall net worth series.
  const [showFireSettings, setShowFireSettings] = useState(false);
  const [expensesDraft, setExpensesDraft] = useState('');
  const [rateDraft, setRateDraft] = useState('4');

  useEffect(() => {
    setExpensesDraft(fireSettings?.annualExpenses ? String(fireSettings.annualExpenses) : '');
    setRateDraft(fireSettings?.withdrawalRate ? String(fireSettings.withdrawalRate * 100) : '4');
  }, [fireSettings]);

  const fireEligibleAssetNames = useMemo(
    () => accounts.assets.filter(a => a.includeInFire !== false).map(a => a.name),
    [accounts]
  );

  // One value per sortedMonthlyData entry: selected assets' values that month
  // (asset-only, not net of debt). Growth is deliberately computed from this
  // rather than from (assets - debts): netting a roughly-flat debt against a
  // shrinking asset base amplifies the % growth rate wildly (a leverage
  // effect on a small equity base), so unchecking an asset could make the
  // remaining, smaller base show a *higher* growth rate and a *shorter*
  // time-to-FI — backwards from what unchecking an asset should do.
  const fireEligibleAssetSeries = useMemo(() => {
    if (!hasHistory) return [];
    return sortedMonthlyData.map(m =>
      fireEligibleAssetNames.reduce((sum, name) => sum + (parseFloat(m.values[name]) || 0), 0)
    );
  }, [hasHistory, sortedMonthlyData, fireEligibleAssetNames]);

  const fireGrowthInfo = useMemo(() => getGrowthInfo(fireEligibleAssetSeries), [fireEligibleAssetSeries]);

  const fireCurrentAssets = fireEligibleAssetSeries.length ? fireEligibleAssetSeries[fireEligibleAssetSeries.length - 1] : null;
  const fireCurrentDebts = hasHistory ? (parseFloat(latestData.totalDebts) || 0) : 0;
  const fireCurrentNetWorth = fireCurrentAssets != null ? fireCurrentAssets - fireCurrentDebts : null;

  const fireNumber = calculateFireNumber(fireSettings?.annualExpenses, fireSettings?.withdrawalRate);
  const fireProgressPct = fireNumber && fireCurrentNetWorth != null ? (fireCurrentNetWorth / fireNumber) * 100 : null;
  // Project the (debt-free) asset growth forward, then require it to cover
  // the FIRE number plus today's debts — equivalent to projecting net worth,
  // but without compounding debt reduction into the growth assumption.
  const timeToFire = fireCurrentAssets != null && fireNumber != null
    ? calculateTimeToFire(fireCurrentAssets, fireNumber + fireCurrentDebts, fireGrowthInfo)
    : null;

  const timeToFireLabel = useMemo(() => {
    if (!timeToFire) return null;
    if (timeToFire.reached) return 'Reached! 🎉';
    if (timeToFire.months == null) return 'Not yet on track';
    const years = Math.floor(timeToFire.months / 12);
    const rem = timeToFire.months % 12;
    const [y, mo] = latestData.month.split('-').map(Number);
    const targetDate = new Date(y, mo - 1 + timeToFire.months);
    const dateLabel = targetDate.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
    const durationLabel = years > 0 ? `${years}y ${rem}mo` : `${rem}mo`;
    return `${durationLabel} · ${dateLabel}`;
  }, [timeToFire, latestData]);

  const saveFireSettings = () => {
    const expenses = parseFloat(expensesDraft);
    const ratePct = parseFloat(rateDraft);
    if (!expenses || expenses <= 0) {
      alert('Enter a valid annual expenses amount.');
      return;
    }
    if (!ratePct || ratePct <= 0 || ratePct > 20) {
      alert('Enter a withdrawal rate between 0 and 20%.');
      return;
    }
    updateFireSettings({ annualExpenses: expenses, withdrawalRate: ratePct / 100 });
    trackEvent('fire_goal_set');
    setShowFireSettings(false);
  };

  const assetsGrowthInfo = useMemo(() => {
    if (!hasHistory || !chartData) return null;
    return getGrowthInfo(chartData.map(d => d['Assets']));
  }, [hasHistory, chartData]);

  const debtsGrowthInfo = useMemo(() => {
    if (!hasHistory || !chartData) return null;
    return getGrowthInfo(chartData.map(d => d['Debts']));
  }, [hasHistory, chartData]);

  // Build projected data points, thinned for long horizons
  const projectionPoints = useMemo(() => {
    if (projectionYears === 0 || !growthInfo) return [];
    const last = sortedMonthlyData[sortedMonthlyData.length - 1];
    const startValue = selectedAccount === 'overall'
      ? last.netWorth
      : (accountChartData?.[accountChartData.length - 1]?.value ?? 0);
    const [y, mo] = last.month.split('-').map(Number);

    const isOverall = selectedAccount === 'overall';
    const startAssets = isOverall && chartData ? chartData[chartData.length - 1]['Assets'] : null;
    const startDebts = isOverall && chartData ? chartData[chartData.length - 1]['Debts'] : null;

    const totalMonths = projectionYears * 12;
    const step = projectionYears > 5 ? 12 : projectionYears > 2 ? 3 : 1;

    const points = [];
    let value = startValue;
    let assets = startAssets;
    let debts = startDebts;
    for (let m = 1; m <= totalMonths; m++) {
      value = growthInfo.type === 'rate' ? value * (1 + growthInfo.monthly) : value + growthInfo.monthly;
      if (assets !== null && assetsGrowthInfo) {
        assets = assetsGrowthInfo.type === 'rate' ? assets * (1 + assetsGrowthInfo.monthly) : assets + assetsGrowthInfo.monthly;
      }
      if (debts !== null && debtsGrowthInfo) {
        debts = debtsGrowthInfo.type === 'rate' ? debts * (1 + debtsGrowthInfo.monthly) : debts + debtsGrowthInfo.monthly;
      }
      if (m % step === 0 || m === totalMonths) {
        const date = new Date(y, mo - 1 + m);
        points.push({
          month: date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' }),
          value,
          simulatedAssets: assets,
          simulatedDebts: debts,
        });
      }
    }
    return points;
  }, [projectionYears, growthInfo, assetsGrowthInfo, debtsGrowthInfo, sortedMonthlyData, selectedAccount, accountChartData, chartData]);

  // Merge real + projected into a single array for each chart type
  const combinedOverallData = useMemo(() => {
    if (!chartData) return null;
    if (!projectionPoints.length) return chartData;
    const real = chartData.map((d, i) => ({
      ...d,
      simulated: i === chartData.length - 1 ? d['Net Worth'] : undefined,
      simulatedAssets: i === chartData.length - 1 ? d['Assets'] : undefined,
      simulatedDebts: i === chartData.length - 1 ? d['Debts'] : undefined,
    }));
    const future = projectionPoints.map(p => ({
      month: p.month,
      'Net Worth': undefined, Assets: undefined, Debts: undefined,
      simulated: p.value,
      simulatedAssets: p.simulatedAssets,
      simulatedDebts: p.simulatedDebts,
    }));
    return [...real, ...future];
  }, [chartData, projectionPoints]);

  const combinedAccountData = useMemo(() => {
    if (!accountChartData) return null;
    if (!projectionPoints.length) return accountChartData;
    const real = accountChartData.map((d, i) => ({
      ...d,
      simulated: i === accountChartData.length - 1 ? d.value : undefined,
    }));
    const future = projectionPoints.map(p => ({
      displayMonth: p.month, value: undefined, simulated: p.value,
    }));
    return [...real, ...future];
  }, [accountChartData, projectionPoints]);

  if (accounts.assets.length === 0 && accounts.debts.length === 0) {
    return (
      <div className="text-center py-20">
        <p className="text-gray-500 text-sm mb-6">Add accounts to start tracking your financial journey.</p>
        <button
          onClick={() => setShowAccountManager(true)}
          className="inline-flex items-center gap-2 px-5 py-2 bg-accent text-white rounded-xl text-sm font-medium hover:bg-emerald-700 transition-colors shadow-sm"
        >
          <Plus className="w-4 h-4" />
          Add First Account
        </button>
      </div>
    );
  }

  const totalAssets = accounts.assets.reduce((sum, a) => sum + (parseFloat(entryData[a.name]) || 0), 0);
  const totalDebts = accounts.debts.reduce((sum, a) => sum + (parseFloat(entryData[a.name]) || 0), 0);
  const netWorth = totalAssets - totalDebts;

  return (
    <>
    <div className="max-w-4xl mx-auto space-y-6">

      {/* Entry section */}
      {!selectedMonth && !editMode ? (
        <div className="text-center py-10 bg-white rounded-2xl border border-gray-100 shadow-card">
          <h2 className="text-gray-900 font-semibold text-lg tracking-tight mb-1.5">Enter Monthly Data</h2>
          <p className="text-gray-500 text-sm mb-6">Record your account balances to track net worth over time.</p>
          <div className="flex flex-col sm:flex-row gap-2 justify-center items-center">
            <button
              onClick={() => startNewEntry()}
              className="inline-flex items-center gap-2 px-5 py-2 bg-accent text-white rounded-xl text-sm font-medium hover:bg-emerald-700 transition-colors shadow-sm"
            >
              <Plus className="w-4 h-4" />
              Enter Current Month
            </button>
            <button
              onClick={() => setShowMonthPicker(true)}
              className="inline-flex items-center gap-2 px-5 py-2 border border-gray-200 rounded-xl text-gray-600 text-sm hover:text-gray-900 hover:bg-gray-50 transition-colors"
            >
              <ChevronDown className="w-4 h-4" />
              Choose Month
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-1.5">
                <h2 className="text-gray-900 font-semibold text-lg tracking-tight">{formatMonthKey(selectedMonth)}</h2>
                {editMode && (
                  <button
                    onClick={() => setShowMonthPicker(true)}
                    className="p-1 text-gray-400 hover:text-accent transition-colors"
                    title="Change month"
                  >
                    <Calendar className="w-4 h-4" />
                  </button>
                )}
              </div>
              <p className="text-xs text-gray-400 uppercase tracking-wide mt-0.5">Financial Snapshot</p>
            </div>
            <div className="flex gap-2">
              {editMode ? (
                <>
                  <button
                    onClick={saveMonthData}
                    disabled={saving}
                    className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-accent text-white rounded-lg text-sm font-medium hover:bg-emerald-700 transition-colors disabled:opacity-50"
                  >
                    {saving ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                    {saving ? 'Saving…' : 'Save'}
                  </button>
                  <button
                    onClick={cancelEdit}
                    className="inline-flex items-center gap-1.5 px-4 py-1.5 text-gray-500 hover:text-gray-900 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <X className="w-3.5 h-3.5" />
                    Cancel
                  </button>
                </>
              ) : (
                <button
                  onClick={() => startNewEntry(selectedMonth)}
                  className="px-4 py-1.5 text-sm text-accent hover:text-emerald-700 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                >
                  Edit
                </button>
              )}
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4">
            <StatCard label="Assets" value={formatCurrency(totalAssets)} />
            <StatCard label="Debts" value={formatCurrency(totalDebts)} />
            <StatCard label="Net Worth" value={formatCurrency(netWorth)} tone={netWorth > 0 ? 'positive' : undefined} />
          </div>

          {/* Account inputs */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {accounts.assets.length > 0 && (
              <div className="bg-white rounded-2xl border border-gray-100 shadow-card overflow-hidden">
                <div className="px-5 py-3 border-b border-gray-100">
                  <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Assets</span>
                </div>
                <AccountRows items={accounts.assets} editMode={editMode} entryData={entryData} priorValues={priorValues} handleValueChange={handleValueChange} />
              </div>
            )}
            {accounts.debts.length > 0 && (
              <div className="bg-white rounded-2xl border border-gray-100 shadow-card overflow-hidden">
                <div className="px-5 py-3 border-b border-gray-100">
                  <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Debts</span>
                </div>
                <AccountRows items={accounts.debts} editMode={editMode} entryData={entryData} priorValues={priorValues} handleValueChange={handleValueChange} />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Performance section */}
      {hasHistory && (() => {
        const lastRealOverall = chartData?.[chartData.length - 1]?.month;
        const lastRealAccount = accountChartData?.[accountChartData.length - 1]?.displayMonth;
        const growthLabel = growthInfo
          ? growthInfo.type === 'rate'
            ? `~${growthInfo.annual >= 0 ? '+' : ''}${growthInfo.annual.toFixed(1)}% / yr (historical avg.)`
            : `~${growthInfo.monthly >= 0 ? '+' : ''}${formatCurrency(growthInfo.monthly)} / mo (historical avg.)`
          : null;

        return (
          <div className="space-y-5 border-t border-gray-100 pt-6">
            {/* Controls row */}
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
              <div className="flex items-center gap-3 pt-0.5">
                <h2 className="text-gray-900 font-semibold text-lg tracking-tight">Performance</h2>
                <button
                  onClick={() => { trackEvent('ai_summary_viewed'); setShowAISummary(true); }}
                  className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-accent transition-colors"
                >
                  <Sparkles className="w-3 h-3" />
                  AI Summary
                </button>
              </div>
              <div className="flex flex-col sm:items-end gap-1.5">
                <div className="flex gap-2">
                  <div className="flex bg-gray-100 rounded-lg p-0.5">
                    {CHART_RANGES.map(range => (
                      <button
                        key={range.key}
                        onClick={() => { trackEvent('chart_range_changed', { range: range.key }); setChartRange(range.key); }}
                        className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
                          chartRange === range.key
                            ? 'bg-white text-gray-900 shadow-sm'
                            : 'text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        {range.label}
                      </button>
                    ))}
                  </div>
                  <select
                    value={selectedAccount}
                    onChange={e => setSelectedAccount(e.target.value)}
                    className="bg-white border border-gray-200 rounded-lg text-gray-700 text-sm px-3 py-1.5 outline-none focus:border-accent"
                  >
                    <option value="overall">Overall</option>
                    {accounts.assets.length > 0 && (
                      <optgroup label="Assets">
                        {accounts.assets.map(a => <option key={a.id} value={a.name}>{a.name}</option>)}
                      </optgroup>
                    )}
                    {accounts.debts.length > 0 && (
                      <optgroup label="Debts">
                        {accounts.debts.map(a => <option key={a.id} value={a.name}>{a.name}</option>)}
                      </optgroup>
                    )}
                    {categories.length > 0 && (
                      <optgroup label="Categories">
                        {categories.map(c => <option key={c} value={`cat:${c}`}>{c}</option>)}
                      </optgroup>
                    )}
                  </select>
                  <select
                    value={projectionYears}
                    onChange={e => setProjectionYears(Number(e.target.value))}
                    className="bg-white border border-gray-200 rounded-lg text-gray-700 text-sm px-3 py-1.5 outline-none focus:border-accent"
                  >
                    <option value={0}>No simulation</option>
                    {[1,2,3,5,10,15,20].map(y => (
                      <option key={y} value={y}>+{y} {y === 1 ? 'year' : 'years'}</option>
                    ))}
                  </select>
                </div>
                {projectionYears > 0 && growthLabel && (
                  <p className="text-xs text-gray-400">{growthLabel}</p>
                )}
              </div>
            </div>

            {selectedAccount === 'overall' ? (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <StatCard label="Current Net Worth" value={formatCurrency(latestData.netWorth)} tone="positive" />
                  <StatCard label="Total Growth" value={formatCurrency(totalGrowth)} tone={totalGrowth >= 0 ? 'positive' : 'negative'} />
                  <StatCard label="Growth Rate" value={growthPct !== null ? `${totalGrowth >= 0 ? '+' : ''}${growthPct}%` : '—'} tone={totalGrowth >= 0 ? 'positive' : 'negative'} />
                  <StatCard label="Months Tracked" value={rangedMonthlyData.length} />
                </div>

                <div className="bg-white rounded-2xl border border-gray-100 shadow-card p-6">
                  <div className="flex items-center justify-between mb-4">
                    <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">FIRE Progress</p>
                    <button
                      onClick={() => setShowFireSettings(v => !v)}
                      className="text-xs text-gray-400 hover:text-accent transition-colors font-medium"
                    >
                      {fireNumber ? 'Edit' : 'Set up'}
                    </button>
                  </div>

                  {showFireSettings && (
                    <div className="mb-5 p-4 bg-gray-50 border border-gray-200 rounded-xl space-y-3">
                      <div className="flex items-center gap-3">
                        <label className="text-sm text-gray-600 w-32 shrink-0">Annual expenses</label>
                        <div className="relative flex-1">
                          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                          <input
                            type="number"
                            value={expensesDraft}
                            onChange={e => setExpensesDraft(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && saveFireSettings()}
                            placeholder="60000"
                            className="w-full pl-6 pr-2 py-1.5 text-sm bg-white border border-gray-200 rounded-lg outline-none focus:border-accent focus:ring-2 focus:ring-emerald-100"
                          />
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <label className="text-sm text-gray-600 w-32 shrink-0">Withdrawal rate</label>
                        <div className="relative flex-1">
                          <input
                            type="number"
                            step="0.1"
                            value={rateDraft}
                            onChange={e => setRateDraft(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && saveFireSettings()}
                            placeholder="4"
                            className="w-full pl-2 pr-7 py-1.5 text-sm bg-white border border-gray-200 rounded-lg outline-none focus:border-accent focus:ring-2 focus:ring-emerald-100"
                          />
                          <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm">%</span>
                        </div>
                      </div>

                      {accounts.assets.length > 0 && (
                        <div>
                          <label className="text-sm text-gray-600 block mb-1.5">Assets to include</label>
                          <div className="space-y-1.5 max-h-40 overflow-y-auto pr-1">
                            {accounts.assets.map(a => {
                              const checked = a.includeInFire !== false;
                              return (
                                <label key={a.id} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => toggleFireAsset(a.name)}
                                    className="w-4 h-4 rounded border-gray-300 text-accent focus:ring-accent focus:ring-offset-0"
                                  />
                                  {DOMPurify.sanitize(a.name)}
                                </label>
                              );
                            })}
                          </div>
                          <p className="text-xs text-gray-400 mt-1.5">Uncheck accounts that aren't part of your own retirement, like a child's 529 plan.</p>
                        </div>
                      )}

                      <button
                        onClick={saveFireSettings}
                        className="px-3 py-1.5 bg-accent text-white rounded-lg text-xs font-medium hover:bg-emerald-700 transition-colors"
                      >
                        Save
                      </button>
                    </div>
                  )}

                  {fireNumber ? (
                    <>
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div>
                          <p className="text-[11px] text-gray-400 uppercase tracking-wide mb-1">FIRE Number</p>
                          <p className="text-lg font-semibold tracking-tight text-gray-900">{formatCurrency(fireNumber)}</p>
                        </div>
                        <div>
                          <p className="text-[11px] text-gray-400 uppercase tracking-wide mb-1">Time to FI</p>
                          <p className="text-lg font-semibold tracking-tight text-gray-900">{timeToFireLabel ?? '—'}</p>
                        </div>
                      </div>
                      <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-accent rounded-full transition-all"
                          style={{ width: `${Math.min(100, Math.max(0, fireProgressPct))}%` }}
                        />
                      </div>
                      <p className="text-xs text-gray-400 mt-1.5">
                        {fireProgressPct >= 100
                          ? `${fireProgressPct.toFixed(0)}% of your FIRE number — you're there!`
                          : `${Math.max(0, fireProgressPct).toFixed(1)}% of the way to your FIRE number`}
                      </p>
                    </>
                  ) : !showFireSettings ? (
                    <p className="text-sm text-gray-400">Set your annual expenses to calculate your FIRE number and estimated time to financial independence.</p>
                  ) : null}
                </div>

                <div className="bg-white rounded-2xl border border-gray-100 shadow-card p-6">
                  <div className="flex items-center justify-between mb-4">
                    <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">Net Worth Trend</p>
                    {projectionYears > 0 && (
                      <div className="flex items-center gap-3 text-xs text-gray-400">
                        <span className="flex items-center gap-1.5"><span className="inline-block w-6 border-t-2 border-accent"></span>Actual</span>
                        <span className="flex items-center gap-1.5"><span className="inline-block w-6 border-t-2 border-accent border-dashed opacity-50"></span>Simulated</span>
                      </div>
                    )}
                  </div>
                  <ResponsiveContainer width="100%" height={260}>
                    <LineChart data={combinedOverallData}>
                      <CartesianGrid strokeDasharray="3 3" stroke={CHART_STYLE.gridStroke} />
                      <XAxis dataKey="month" angle={-45} textAnchor="end" height={70} stroke={CHART_STYLE.axisStroke} tick={{ fill: CHART_STYLE.tickFill, fontSize: 11 }} />
                      <YAxis stroke={CHART_STYLE.axisStroke} tick={{ fill: CHART_STYLE.tickFill, fontSize: 11 }} />
                      <Tooltip formatter={v => formatCurrency(v)} contentStyle={CHART_STYLE.contentStyle} />
                      <Legend wrapperStyle={{ fontSize: '11px', color: '#9ca3af' }} />
                      <Line type="monotone" dataKey="Net Worth" stroke={NET_WORTH_COLOR} strokeWidth={2} dot={{ fill: NET_WORTH_COLOR, r: 3 }} connectNulls={false} />
                      <Line type="monotone" dataKey="Assets" stroke={ASSETS_COLOR} strokeWidth={1.5} dot={{ fill: ASSETS_COLOR, r: 2 }} connectNulls={false} />
                      <Line type="monotone" dataKey="Debts" stroke={DEBTS_COLOR} strokeWidth={1.5} dot={{ fill: DEBTS_COLOR, r: 2 }} connectNulls={false} />
                      {projectionYears > 0 && <Line type="monotone" dataKey="simulated" name="Simulated" stroke={NET_WORTH_COLOR} strokeWidth={2} strokeDasharray="6 4" strokeOpacity={0.45} dot={false} connectNulls={false} legendType="none" />}
                      {projectionYears > 0 && <Line type="monotone" dataKey="simulatedAssets" stroke={ASSETS_COLOR} strokeWidth={1.5} strokeDasharray="6 4" strokeOpacity={0.35} dot={false} connectNulls={false} legendType="none" />}
                      {projectionYears > 0 && <Line type="monotone" dataKey="simulatedDebts" stroke={DEBTS_COLOR} strokeWidth={1.5} strokeDasharray="6 4" strokeOpacity={0.35} dot={false} connectNulls={false} legendType="none" />}
                      {projectionYears > 0 && lastRealOverall && <ReferenceLine x={lastRealOverall} stroke="#d1d5db" strokeDasharray="4 4" label={{ value: 'now', position: 'insideTopRight', fill: '#9ca3af', fontSize: 10 }} />}
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                {monthlyChange && (
                  <div className="bg-white rounded-2xl border border-gray-100 shadow-card p-6">
                    <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-4">Monthly Changes</p>
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={monthlyChange}>
                        <CartesianGrid strokeDasharray="3 3" stroke={CHART_STYLE.gridStroke} />
                        <XAxis dataKey="month" angle={-45} textAnchor="end" height={70} stroke={CHART_STYLE.axisStroke} tick={{ fill: CHART_STYLE.tickFill, fontSize: 11 }} />
                        <YAxis stroke={CHART_STYLE.axisStroke} tick={{ fill: CHART_STYLE.tickFill, fontSize: 11 }} />
                        <Tooltip formatter={v => formatCurrency(v)} contentStyle={CHART_STYLE.contentStyle} />
                        <Bar dataKey="change" fill={NET_WORTH_COLOR} radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </>
            ) : accountChartData && accountChartData.length > 0 ? (
              <>
                {accountStats && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <StatCard label="Current Value" value={formatCurrency(accountStats.current)} tone="positive" />
                    <StatCard label="Total Change" value={formatCurrency(accountStats.change)} tone={accountStats.change >= 0 ? 'positive' : 'negative'} />
                    <StatCard label="Change Rate" value={accountStats.pct !== null ? `${accountStats.change >= 0 ? '+' : ''}${accountStats.pct}%` : '—'} tone={accountStats.change >= 0 ? 'positive' : 'negative'} />
                    <StatCard label="Months Tracked" value={accountStats.months} />
                  </div>
                )}

                <div className="bg-white rounded-2xl border border-gray-100 shadow-card p-6">
                  <div className="flex items-center justify-between mb-4">
                    <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">{selectedAccountLabel} — Value Over Time</p>
                    {projectionYears > 0 && (
                      <div className="flex items-center gap-3 text-xs text-gray-400">
                        <span className="flex items-center gap-1.5"><span className="inline-block w-6 border-t-2 border-accent"></span>Actual</span>
                        <span className="flex items-center gap-1.5"><span className="inline-block w-6 border-t-2 border-accent border-dashed opacity-50"></span>Simulated</span>
                      </div>
                    )}
                  </div>
                  <ResponsiveContainer width="100%" height={260}>
                    <LineChart data={combinedAccountData}>
                      <CartesianGrid strokeDasharray="3 3" stroke={CHART_STYLE.gridStroke} />
                      <XAxis dataKey="displayMonth" angle={-45} textAnchor="end" height={70} stroke={CHART_STYLE.axisStroke} tick={{ fill: CHART_STYLE.tickFill, fontSize: 11 }} />
                      <YAxis stroke={CHART_STYLE.axisStroke} tick={{ fill: CHART_STYLE.tickFill, fontSize: 11 }} />
                      <Tooltip formatter={v => formatCurrency(v)} contentStyle={CHART_STYLE.contentStyle} />
                      <Line type="monotone" dataKey="value" name={selectedAccountLabel} stroke={NET_WORTH_COLOR} strokeWidth={2} dot={{ fill: NET_WORTH_COLOR, r: 3 }} connectNulls={false} />
                      {projectionYears > 0 && <Line type="monotone" dataKey="simulated" name="Simulated" stroke={NET_WORTH_COLOR} strokeWidth={2} strokeDasharray="6 4" strokeOpacity={0.45} dot={false} connectNulls={false} />}
                      {projectionYears > 0 && lastRealAccount && <ReferenceLine x={lastRealAccount} stroke="#d1d5db" strokeDasharray="4 4" label={{ value: 'now', position: 'insideTopRight', fill: '#9ca3af', fontSize: 10 }} />}
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                {accountMonthlyChange && (
                  <div className="bg-white rounded-2xl border border-gray-100 shadow-card p-6">
                    <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-4">Monthly Changes</p>
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={accountMonthlyChange}>
                        <CartesianGrid strokeDasharray="3 3" stroke={CHART_STYLE.gridStroke} />
                        <XAxis dataKey="displayMonth" angle={-45} textAnchor="end" height={70} stroke={CHART_STYLE.axisStroke} tick={{ fill: CHART_STYLE.tickFill, fontSize: 11 }} />
                        <YAxis stroke={CHART_STYLE.axisStroke} tick={{ fill: CHART_STYLE.tickFill, fontSize: 11 }} />
                        <Tooltip formatter={v => formatCurrency(v)} contentStyle={CHART_STYLE.contentStyle} />
                        <Bar dataKey="change" fill={NET_WORTH_COLOR} radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </>
            ) : (
              <div className="bg-white rounded-2xl border border-gray-100 shadow-card px-5 py-8 text-center">
                <p className="text-sm text-gray-400">No data recorded for <span className="text-gray-600">{selectedAccountLabel}</span> yet.</p>
              </div>
            )}
          </div>
        );
      })()}
    </div>

    {showAISummary && hasHistory && (
      <AISummaryModal
        onClose={() => setShowAISummary(false)}
        data={{
          currentMonth: latestData,
          previousMonth: sortedMonthlyData.length > 1 ? sortedMonthlyData[sortedMonthlyData.length - 2] : null,
          allMonths: sortedMonthlyData,
          accounts,
        }}
      />
    )}
    </>
  );
};

export default EntryView;
